import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { logAction } from '../utils/scoringLog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import BackspaceIcon from '@mui/icons-material/Backspace';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import DoneIcon from '@mui/icons-material/Done';
import { useTranslation } from 'react-i18next';
import PlusMinus from './PlusMinus';
import ScoringBluetooth from './ScoringBluetooth';

const isAppMode = () => !!(window?.ReactNativeWebView || window?.Android || window?.webkit?.messageHandlers?.ipscAppMessageHandler);

// Pre-load beep sounds (pool of 5 to handle rapid clicks)
let beepPool = null;
let beep2Pool = null;
let soundIdx = 0;

function beep({ major, decrease } = {}) {
  if (localStorage.getItem('__scoring_mute') === '1') return;
  if (!beepPool) {
    beepPool = Array.from({ length: 5 }, () => new Audio('/beep.wav'));
    beep2Pool = Array.from({ length: 5 }, () => new Audio('/beep2.wav'));
  }
  const pool = decrease ? beep2Pool : beepPool;
  pool[soundIdx++ % pool.length].play().catch(() => {});
  if (major) {
    setTimeout(() => {
      pool[soundIdx++ % pool.length].play().catch(() => {});
    }, 150);
  }
  if (navigator.vibrate) navigator.vibrate(200);
}

export default function ScoreForm({ stage, scoreData, onChange, scoreMode = 'comstock' }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  const targets = stage?.targets || {};
  const numPaper = (targets.paper || 0) + (targets.minipaper || 0);
  const numMetalTargets = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0) + (targets['pcc-frangible'] || 0);
  const numTenPointsTargets = (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0);
  const totalRounds = stage?.rounds || 0;
  const bonusTargets = stage?.bonusTargets || [];

  const shotsPerTarget = useMemo(() => {
    if (numPaper === 0) return 0;
    return (totalRounds - numMetalTargets - numTenPointsTargets) / numPaper;
  }, [totalRounds, numPaper, numMetalTargets, numTenPointsTargets]);

  const [currentTarget, setCurrentTarget] = useState(0);
  const [innerTime, setInnerTime] = useState(() => {
    if (!scoreData.time) return '';
    const s = String(scoreData.time);
    if (s.includes('.')) { const p = s.split('.'); return `${p[0]}.${(p[1] || '').padEnd(2, '0').slice(0, 2)}`; }
    return `${s}.00`;
  });
  const timeTypingRef = useRef(false);
  // Sync innerTime when scoreData.time changes externally (e.g. cache restore)
  useEffect(() => {
    if (timeTypingRef.current) { timeTypingRef.current = false; return; }
    if (!scoreData.time) { setInnerTime(''); return; }
    const s = String(scoreData.time);
    if (s.includes('.')) { const p = s.split('.'); setInnerTime(`${p[0]}.${(p[1] || '').padEnd(2, '0').slice(0, 2)}`); }
    else setInnerTime(`${s}.00`);
  }, [scoreData.time]);
  const [timeError, setTimeError] = useState(false);
  const [showBluetooth, setShowBluetooth] = useState(() => !!localStorage.getItem('__scoring_bluetooth_cache'));

  const isCurrentTargetBonus = bonusTargets.includes(currentTarget);
  const scorePerTarget = scoreData.scorePerTarget || [];
  const currentTargetScore = scorePerTarget[currentTarget] || '';
  const paperScoringDone = numPaper > 0 && Array.from({ length: numPaper }).every((_, i) => (scorePerTarget[i] || '').length === shotsPerTarget);

  const handleFieldChange = useCallback((field, value) => {
    onChange({ ...scoreData, [field]: value });
  }, [scoreData, onChange]);

  const handleMetalChange = useCallback((field, value) => {
    const prev = scoreData[field] || 0;
    beep({ decrease: value < prev });
    logAction('METAL_CHANGE', { stageIdx: stage?.stageIdx }, { field, from: prev, to: value });
    onChange({ ...scoreData, [field]: value });
  }, [scoreData, onChange]);

  const handleTargetHit = useCallback((hit) => {
    const current = currentTargetScore;
    if (current.length >= shotsPerTarget) return;
    const newScore = current + hit;
    const isFull = newScore.length >= shotsPerTarget;
    beep({ major: isFull });
    const newTargets = [...scorePerTarget];
    while (newTargets.length <= currentTarget) newTargets.push('');
    newTargets[currentTarget] = newScore;
    logAction('TARGET_HIT', { stageIdx: stage?.stageIdx }, { target: currentTarget + 1, hit, score: newScore });
    onChange({ ...scoreData, scorePerTarget: newTargets });

    // Auto-advance to next target if all shots filled
    if (isFull && currentTarget < numPaper - 1) {
      setCurrentTarget(currentTarget + 1);
    }
  }, [currentTarget, currentTargetScore, shotsPerTarget, scorePerTarget, scoreData, onChange, numPaper]);

  const handleEraseTarget = useCallback(() => {
    const current = currentTargetScore;
    if (current.length === 0) return;
    beep({ decrease: true });
    const newTargets = [...scorePerTarget];
    newTargets[currentTarget] = current.slice(0, -1);
    logAction('TARGET_ERASE', { stageIdx: stage?.stageIdx }, { target: currentTarget + 1, was: current, now: newTargets[currentTarget] });
    onChange({ ...scoreData, scorePerTarget: newTargets });
  }, [currentTarget, currentTargetScore, scorePerTarget, scoreData, onChange]);

  const parseTimeInput = useCallback((raw) => {
    let timeWithDecimal = raw.replace(/-/g, '');
    if (timeWithDecimal.length > 2 && !timeWithDecimal.includes('.') && !timeWithDecimal.includes(',')) {
      const val = parseInt(raw, 10);
      if (val % 100 === 0) {
        timeWithDecimal = `${val / 100}.00`;
      } else if (val % 10 === 0) {
        timeWithDecimal = `${val / 100}0`;
      } else {
        timeWithDecimal = `${val / 100}`;
      }
    }
    return timeWithDecimal.replace(',', '.');
  }, []);

  const handleTimeBlur = useCallback(() => {
    const timeWithDecimal = parseTimeInput(innerTime);
    setInnerTime(timeWithDecimal);
    setTimeError(false);
    handleFieldChange('time', parseFloat(timeWithDecimal) || 0);
  }, [innerTime, handleFieldChange, parseTimeInput]);

  const handleBluetoothTime = useCallback((value) => {
    setInnerTime(String(value));
    handleFieldChange('time', parseFloat(value) || 0);
  }, [handleFieldChange]);

  const hitButtons = scoreMode === 'comstock-abd'
    ? ['A', 'B', 'D', isCurrentTargetBonus ? 'm' : 'M']
    : scoreMode === 'il-army'
      ? ['A', 'C', isCurrentTargetBonus ? 'm' : 'M']
      : ['A', 'C', 'D', isCurrentTargetBonus ? 'm' : 'M'];

  // Calculate no-shoot max
  const noShootMax = targets.noshoots === 'single' ? 2
    : targets.noshoots === 'several' ? 100
      : ((targets['paper-no-shoots'] || 0) * 2) + (targets['metal-no-shoots'] || 0);

  return (
    <Box>
      {/* ---- TIME ---- */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{t('results.time')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isAppMode() && <IconButton sx={{ visibility: 'hidden' }}><BluetoothSearchingIcon /></IconButton>}
          <TextField
            autoFocus
            placeholder={t('results.time')}
            value={innerTime}
            error={timeError}
            onChange={(e) => { setTimeError(false); setInnerTime(e.target.value); timeTypingRef.current = true; handleFieldChange('time', parseFloat(parseTimeInput(e.target.value)) || 0); }}
            onBlur={handleTimeBlur}
            slotProps={{ input: { inputProps: { inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*', style: { textAlign: 'center', fontSize: '1.5em' } } } }}
            sx={{ flex: 1, maxWidth: 300 }}
          />
          {isAppMode() && (
            <IconButton onClick={() => setShowBluetooth(!showBluetooth)}>
              <BluetoothSearchingIcon />
            </IconButton>
          )}
        </Box>
        <ScoringBluetooth open={showBluetooth} onUpdateScore={handleBluetoothTime} />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ---- POPPERS ---- */}
      {numMetalTargets > 0 && (
        <>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>{t('results.poppers')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 1 }}>
            <PlusMinus
              value={scoreData.poppers || 0}
              onChange={(v) => handleMetalChange('poppers', v)}
              min={0} max={numMetalTargets} size="large"
            />
            {/* Popper icon */}
            <Box sx={{ opacity: 0.3, position: 'relative' }}>
              <Box sx={{ width: 25, height: 25, borderRadius: '50%', bgcolor: 'white', border: '1px solid black' }} />
              <Box sx={{ width: 9, height: 55, bgcolor: 'white', mx: 'auto', border: '1px solid black', borderTop: '1px solid white', position: 'relative', top: -10, transform: 'perspective(300px) rotateX(-45deg)' }} />
            </Box>
          </Box>
          <Typography variant="caption" display="block" sx={{ textAlign: 'center', mb: 1 }} color="text.secondary">
            {scoreMode === 'steel-no-miss'
              ? t('scoring.platesSummaryNoMiss', { total: numMetalTargets })
              : t('scoring.platesSummary', { total: numMetalTargets, misses: numMetalTargets - (scoreData.poppers || 0) })}
          </Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* ---- TEN POINTS POPPERS ---- */}
      {numTenPointsTargets > 0 && (
        <>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>{t('results.tenPointsPoppers')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 1 }}>
            <PlusMinus
              value={scoreData.tenPointsPoppers || 0}
              onChange={(v) => handleMetalChange('tenPointsPoppers', v)}
              min={0} max={numTenPointsTargets} size="large"
            />
            <Box sx={{ opacity: 0.3, position: 'relative' }}>
              <Box sx={{ width: 25, height: 25, borderRadius: '50%', bgcolor: 'white', border: '1px solid black' }} />
              <Box sx={{ width: 9, height: 55, bgcolor: 'white', mx: 'auto', border: '1px solid black', borderTop: '1px solid white', position: 'relative', top: -10, transform: 'perspective(300px) rotateX(-45deg)' }} />
            </Box>
          </Box>
          <Typography variant="caption" display="block" sx={{ textAlign: 'center', mb: 1 }} color="text.secondary">
            {scoreMode === 'steel-no-miss'
              ? t('scoring.platesSummaryNoMiss', { total: numTenPointsTargets })
              : t('scoring.platesSummary', { total: numTenPointsTargets, misses: numTenPointsTargets - (scoreData.tenPointsPoppers || 0) })}
          </Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* ---- PAPER TARGETS ---- */}
      {numPaper > 0 && (
        <>
          {/* Target navigation header */}
          <Box sx={{ textAlign: 'center', direction: 'ltr' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <IconButton
                disabled={currentTarget === 0}
                onClick={() => setCurrentTarget(currentTarget - 1)}
                sx={{ width: 48, height: 48 }}
              >
                {isRtl ? <NavigateNextIcon /> : <NavigateBeforeIcon />}
              </IconButton>
              <Typography variant="h6">{t('scoring.targets')}</Typography>
              <IconButton
                disabled={currentTarget === numPaper - 1}
                onClick={() => setCurrentTarget(currentTarget + 1)}
                sx={{ width: 48, height: 48 }}
              >
                {isRtl ? <NavigateBeforeIcon /> : <NavigateNextIcon />}
              </IconButton>
            </Box>

            {/* Bonus target alert */}
            {bonusTargets.length > 0 && (
              <Paper sx={{
                width: '100%', p: 1.5, lineHeight: 1.5, mb: 1,
                bgcolor: isCurrentTargetBonus ? '#ffebee' : '#e8f5e9',
                transition: 'background 0.2s ease-in-out',
                direction: i18n.dir(),
              }}>
                <Typography variant="body2">{t('scoring.stageHasBonuses1')}</Typography>
                <Typography variant="body2">{t('scoring.stageHasBonuses2', { targets: bonusTargets.sort((a, b) => a - b).map((ti) => `T${ti + 1}`).join(', ') })}</Typography>
                <Typography variant="body2">
                  {t('scoring.thisIsARegularBonus')}{' '}
                  <strong style={{ color: isCurrentTargetBonus ? '#b71c1c' : '#1b5e20' }}>
                    {isCurrentTargetBonus ? t('scoring.thisIsABonus') : t('scoring.thisIsARegular')}
                  </strong>.
                </Typography>
              </Paper>
            )}

            {/* Hit buttons + target display */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', mt: 1.5, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              {/* A / C / D / M buttons */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', width: 190, justifyContent: 'center' }}>
                {hitButtons.map((hit) => (
                  <Button
                    key={hit}
                    variant="contained"
                    disabled={numPaper === 0}
                    color={hit === 'M' || hit === 'm' ? 'error' : 'primary'}
                    onClick={() => handleTargetHit(hit === 'B' ? 'C' : hit === 'm' ? 'M' : hit)}
                    sx={{
                      minWidth: 40, width: 80, height: 80, m: 0.75,
                      fontSize: '1.5rem', fontWeight: 700,
                      fontStyle: isCurrentTargetBonus ? 'italic' : 'normal',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>{hit}</span>
                      {isCurrentTargetBonus && (
                        <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1 }}>{t('scoring.bonus')}</Typography>
                      )}
                    </Box>
                  </Button>
                ))}
              </Box>

              {/* Target image with current target number */}
              <Box>
                <Box sx={{ position: 'relative', width: 100, height: 120 }}>
                  <Box
                    component="img"
                    src="/classicTarget.png"
                    alt="target"
                    sx={{ width: 100, opacity: 0.3 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                  }}>
                    <Typography sx={{ fontSize: 36, fontWeight: 700, lineHeight: 1 }}>
                      T{currentTarget + 1}
                      {isCurrentTargetBonus && (
                        <Typography component="span" variant="caption" sx={{ display: 'block' }}>{t('scoring.bonus')}</Typography>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: 24 }}>
                      {scoreMode === 'comstock-abd' && currentTargetScore
                        ? currentTargetScore.replace(/C/g, 'B')
                        : (currentTargetScore || '\u00A0')}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  disabled={numPaper === 0}
                  variant="text" color="error"
                  onClick={handleEraseTarget}
                  sx={{ minWidth: 40, width: 60, height: 44, m: 0.75 }}
                >
                  <BackspaceIcon fontSize="large" />
                </Button>
              </Box>
            </Box>

            {/* Target grid showing all targets */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, justifyContent: 'center' }}>
              <Box sx={{
                display: 'flex', flexWrap: 'wrap',
                flexDirection: isRtl ? 'row-reverse' : 'row',
                width: (numPaper < 8 ? numPaper : Math.ceil(numPaper / 2)) * 30,
              }}>
                {Array.from({ length: numPaper }).map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => setCurrentTarget(i)}
                    sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', width: 30,
                      cursor: 'pointer', py: 0.25,
                      bgcolor: i === currentTarget ? 'action.selected' : 'transparent',
                      borderRadius: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: i === currentTarget ? 700 : 400 }}>
                      T{i + 1}
                    </Typography>
                    <Typography variant="caption">
                      {scoreMode === 'comstock-abd' && scorePerTarget[i]
                        ? scorePerTarget[i].replace(/C/g, 'B')
                        : (scorePerTarget[i] || '__')}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {paperScoringDone && (
                <DoneIcon fontSize="large" sx={{ color: 'success.main', ml: 1 }} />
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* ---- BONUS FIELD ---- */}
      {stage?.bonusField && (
        <>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>{stage.bonusField}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <PlusMinus
              value={scoreData.bonusField || 0}
              onChange={(v) => handleFieldChange('bonusField', v)}
              min={0} max={20} size="large"
            />
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* ---- NO-SHOOTS ---- */}
      <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>{t('results.noShoots')}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <PlusMinus
          value={scoreData['no-shoots'] || 0}
          onChange={(v) => handleMetalChange('no-shoots', v)}
          min={0} max={noShootMax || 50} size="large"
        />
      </Box>
      <Typography variant="caption" display="block" sx={{ textAlign: 'center', mb: 1 }} color="text.secondary">
        {t('scoring.noShootsSummary', { total: noShootMax || 0 })}
      </Typography>

    </Box>
  );
}
